import React, { useState } from "react";
import { ArrowUpRight, Mail, Eye, EyeOff, User } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import userAPI from "../../api/UserApi";
import { showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import Breadcrump from "../../components/Breadcrump";

const RegisterCanvas = () => {
  useAlphabeticInput();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ fullName: "", email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please Enter Full Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const [firstName, ...rest] = formData.fullName.trim().split(" ");
      const lastName = rest.join(" ") || firstName;

      const res = await userAPI.register({
        firstName,
        lastName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.password,
      });

      if (res.code === 200) {
        showSuccess(res.message || "Account created successfully");
        setAccessToken(res.data?.accessToken);
        if (res.data?.accessToken) {
          localStorage.setItem(
            "token",
            JSON.stringify({ email: res.data.email, username: res.data.username })
          );
          localStorage.setItem("accessToken", res.data.accessToken);
          navigate("/users/dashboard");
        } else {
          navigate("/users/login");
        }
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrump title="Register" />
      <div className="w-full flex flex-col lg:flex-row overflow-x-hidden" style={{ background: "var(--color-bg)" }}>
        <style>{`
        :root {
          --color-primary: #A6472E;
          --color-primary-dark: #7A2F1E;
          --color-primary-light: #C9694C;

          --color-secondary: #4B3226;
          --color-secondary-light: #6E4A38;

          --color-accent-green: #4A5B3A;
          --color-accent-green-light: #7C8A63;

          --color-accent-gold: #B08D57;
          --color-accent-gold-light: #D4B483;

          --color-tan: #C9A26D;
          --color-tan-light: #E8D6B3;

          --color-bg: #FFFDF8;
          --color-bg-alt: #F6EFE2;
          --color-white: #FFFFFF;
          --color-black: #1A1310;
          --color-text: #2E211A;
          --color-text-muted: #6B5D53;

          --color-border: #E5D9C3;
          --shadow-sm: 0 1px 3px rgba(74, 47, 30, 0.12);
          --shadow-md: 0 4px 12px rgba(74, 47, 30, 0.16);
          --shadow-lg: 0 10px 30px rgba(74, 47, 30, 0.2);

          --font-heading: 'Bebas Neue', cursive;
          --font-body: 'Space Grotesk', sans-serif;
          --font-logo: 'Bebas Neue', cursive;
          --font-subheading: 'Space Grotesk', sans-serif;
          --font-accent: 'Bebas Neue', cursive;
        }

        .lg-grain {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
        }

        .spice-input {
          background: transparent;
          border: none;
          border-bottom: 1.5px solid var(--color-border);
          padding: 12px 28px 12px 0;
          width: 100%;
          // font-family: var(--font-heading);
          font-size: 1rem;
          // font-style: italic;
          color: var(--color-text);
          transition: border-color 0.3s ease, padding-left 0.3s ease;
        }
        .spice-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }
        .spice-input::placeholder { color: transparent; }

        .spice-label {
          position: absolute; left: 0; top: 12px;
          font-family: var(--font-subheading);
          font-size: 0.62rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.18em;
          color: var(--color-text-muted);
          pointer-events: none;
          transition: all 0.28s ease;
        }
        .spice-input:focus ~ .spice-label,
        .spice-input:not(:placeholder-shown) ~ .spice-label {
          top: -11px;
          font-size: 0.54rem;
          color: var(--color-accent-gold);
          letter-spacing: 0.22em;
        }

        .spice-tab {
          padding-bottom: 12px;
          font-family: var(--font-subheading);
          font-size: 0.62rem; font-weight: 700;
          letter-spacing: 0.2em; text-transform: uppercase;
          background: none; border: none; cursor: pointer;
          position: relative; transition: color 0.25s ease;
          text-decoration: none; display: inline-block;
        }

        .spice-submit {
          width: 100%;
          display: flex; align-items: center; justify-content: space-between;
          padding: 15px 22px;
          background: var(--color-primary); color: var(--color-white);
          border: none; border-radius: 2px; cursor: pointer;
          box-shadow: 0 6px 24px rgba(166,60,6,0.32);
          transition: all 0.3s ease;
        }
        .spice-submit:hover { background: var(--color-primary-dark); transform: translateY(-2px); box-shadow: 0 10px 32px rgba(166,60,6,0.40); }
        .spice-submit-label {
          font-family: var(--font-subheading);
          font-size: 0.65rem; font-weight: 700;
          letter-spacing: 0.22em; text-transform: uppercase;
        }
        .spice-submit-icon {
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.25);
          display: flex; align-items: center; justify-content: center;
          transition: all 0.3s ease; flex-shrink: 0;
        }
        .spice-submit:hover .spice-submit-icon {
          background: var(--color-accent-gold);
          border-color: var(--color-accent-gold);
        }

        .gold-seam {
          position: absolute; left: 0; top: 0; bottom: 0;
          width: 1px;
          background: linear-gradient(to bottom, transparent, var(--color-accent-gold), transparent);
          opacity: 0.45;
        }
      `}</style>

        {/* ══ LEFT: Brand Visual — fixed to screen height, sticky while form scrolls ══ */}
        <div className="hidden lg:block lg:w-1/2 lg:min-h-screen lg:sticky lg:top-0 relative flex-shrink-0">
          <img
            src="/images/loginimg.jpeg"
            alt="Spice Market"
            className="absolute w-full h-full object-cover"
          />
          <div style={{ position: "absolute", inset: 0, background: "rgba(122,44,4,0.58)", mixBlendMode: "multiply" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)" }} />

          <div className="absolute inset-0 flex flex-col justify-between p-10 xl:p-14 z-10">
            <div></div>

            <div className="max-w-[380px]" style={{ color: "var(--color-white)" }}>
              <div style={{ width: 36, height: 2, background: "var(--color-accent-gold)", borderRadius: 1, marginBottom: 22 }} />
              <h1 style={{ fontFamily: "var(--font-logo)", fontSize: "clamp(2.4rem, 4vw, 4.2rem)", lineHeight: 0.95, letterSpacing: "-0.025em", marginBottom: 18 }}>
                Taste the{" "}
                <span style={{ fontFamily: "var(--font-accent)", color: "var(--color-accent-gold)", fontSize: "1.1em", display: "block", lineHeight: 1.1 }}>
                  Tradition.
                </span>
              </h1>
              <p style={{ fontFamily: "var(--font-heading)", fontStyle: "italic", fontWeight: 300, fontSize: "1rem", color: "rgba(255,255,255,0.78)", lineHeight: 1.75 }}>
                Sign in to access your curated  dry fruits collection, track orders, and discover rare blends sourced directly from Indian farms.
              </p>
            </div>

            <div className="flex justify-between" />
          </div>
        </div>

        {/* ══ RIGHT: Form — min-h-screen so it matches left panel without extra dangling space ══ */}
        <div className="w-full lg:w-1/2 lg:min-h-screen flex flex-col justify-center relative" style={{ background: "var(--color-bg)" }}>
          <div className="lg-grain absolute inset-0 pointer-events-none" style={{ opacity: 0.55 }} />
          <div className="gold-seam hidden lg:block" />

          <div className="w-full max-w-[420px] mx-auto px-3 sm:px-4 py-10 sm:py-12 relative z-10 box-border">

            <div className="mb-7">
              <h2 style={{ fontFamily: "var(--font-heading)", fontSize: "2.5rem", color: "var(--color-text)", letterSpacing: "0.02em", lineHeight: 1 }}>
                Create Account
              </h2>
            </div>

            {/* Tabs */}
            <div className="flex gap-7 mb-8" style={{ borderBottom: "1px solid var(--color-border)" }}>
              <Link to="/users/login" className="spice-tab" style={{ color: "var(--color-text-muted)" }}>
                Sign In
              </Link>
              <button
                className="spice-tab"
                style={{ color: "var(--color-text)" }}
              >
                Register
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "var(--color-primary)", borderRadius: 1 }} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-7">

              <div style={{ position: "relative", paddingBottom: 4 }}>
                <input type="text" name="fullName" id="fullName" className="spice-input only-alphabets" placeholder=" " value={formData.fullName} onChange={handleChange} />
                <label htmlFor="fullName" className="spice-label">Full Name</label>
                <User size={15} style={{ position: "absolute", right: 0, top: 14, color: "var(--color-text-muted)" }} />
                {errors.fullName && (
                  <span style={{ display: "block", marginTop: 6, fontSize: "0.7rem", color: "#c0392b" }}>{errors.fullName}</span>
                )}
              </div>

              {/* Email */}
              <div style={{ position: "relative", paddingBottom: 4 }}>
                <input type="email" name="email" id="email" className="spice-input" placeholder=" " value={formData.email} onChange={handleChange} required />
                <label htmlFor="email" className="spice-label">Email Address</label>
                <Mail size={15} style={{ position: "absolute", right: 0, top: 14, color: "var(--color-text-muted)" }} />
                {errors.email && (
                  <span style={{ display: "block", marginTop: 6, fontSize: "0.7rem", color: "#c0392b" }}>{errors.email}</span>
                )}
              </div>

              {/* Password */}
              <div style={{ position: "relative", paddingBottom: 4 }}>
                <input type={showPassword ? "text" : "password"} name="password" id="password" className="spice-input" style={{ paddingRight: 28 }} placeholder=" " value={formData.password} onChange={handleChange} required />
                <label htmlFor="password" className="spice-label">Password</label>
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 0, top: 14, background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: 0, display: "flex", alignItems: "center" }}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                {errors.password && (
                  <span style={{ display: "block", marginTop: 6, fontSize: "0.7rem", color: "#c0392b" }}>{errors.password}</span>
                )}
              </div>
              {errors.general && (
                <span style={{ display: "block", fontSize: "0.72rem", color: "#c0392b", marginTop: -14 }}>{errors.general}</span>
              )}

              {/* Submit */}
              <div className="pt-1">
                <button type="submit" className="spice-submit" disabled={loading} style={{ opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
                  <span className="spice-submit-label">
                    {loading ? "Please wait..." : "Create Account"}
                  </span>
                  <div className="spice-submit-icon">
                    <ArrowUpRight size={15} />
                  </div>
                </button>
              </div>

            </form>

            {/* Divider */}
            <div className="flex items-center gap-3.5 my-7">
              <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
              <span style={{ fontFamily: "var(--font-subheading)", fontSize: "0.52rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-text-muted)" }}>already a member</span>
              <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
            </div>

            {/* Login link */}
            <Link
              to="/users/login"
              className="flex items-center justify-center gap-2 px-4 py-2.5"
              style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "var(--color-white)", fontFamily: "var(--font-subheading)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-text)", cursor: "pointer", transition: "all 0.22s ease", textDecoration: "none" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--color-primary)"; e.currentTarget.style.color = "var(--color-primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--color-border)"; e.currentTarget.style.color = "var(--color-text)"; }}
            >
              Sign In
            </Link>

          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterCanvas;